import './consumption-analysis.scss'
import React, {useCallback, useEffect, useMemo, useState} from 'react'
import Button from 'devextreme-react/button'
import DateBox from 'devextreme-react/date-box'
import LoadPanel from 'devextreme-react/load-panel'
import {
    ArgumentAxis,
    Chart,
    CommonSeriesSettings,
    Legend,
    Point,
    Series,
    Strip,
    Tooltip,
} from 'devextreme-react/chart'
import notify from 'devextreme/ui/notify'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {faChartLine} from '@fortawesome/free-solid-svg-icons'
import {useAuth} from '../../contexts/auth'
import {getCurrentPeriod, getGtpFactCodes, getGtpPoints, getPointsTreePlain} from '../../api/dogovor'
import {getEnergosferaCounter, getOutValsSigned} from '../../api/point'
import {dogHasParameterMenu, verifyAccess} from '../../utils/admin.utils'
import {getFirstDayOfMounth, getLastDayOfMounth, periodDayKey, shiftEnergosferaHourLeft} from '../../utils/date.utils'
import {
    calcPowerKpis,
    exportConsumptionExcel,
    isFactPeak,
    isPlanPeak,
    stampPeakFlags,
} from '../../utils/consumption-excel'
import {HOLIDAY_STRIP_COLOR, holidayDateStrips} from '../../utils/peak-chart-marks'
import {useScreenSize} from '../../utils/media-query'
import {getHomeRoute} from '../../utils/navigation.utils'
import {useHistory} from 'react-router-dom'
import AccessDenied from '../../components/access-denied/access-denied'
import ZagolovokFromMenu from '../../components/zagolovok/zagolovok'
import SignPointsTree from '../../components/sign-points-tree/sign-points-tree'
import PopupSignPointsTree from '../../components/sign-points-tree/popup-sign-points-tree'

const FACT_NAME = 'Максимальные часы фактической пиковой нагрузки'
const PLAN_NAME = 'Плановые часы пиковой нагрузки'

/** Слот Энергосферы сдвинут на левую границу часа и отброшен вне выбранных суток. */
function energoSlot(row, period1, period2) {
    const slot = shiftEnergosferaHourLeft(row?.dat_local || row?.time || '')
    if (!slot) return null
    if (slot.dayKey < periodDayKey(period1) || slot.dayKey > periodDayKey(period2)) return null
    return {dat_out: slot.dat_out}
}

/**
 * Сумма почасовых профилей Энергосферы по code_fact — тот же расчёт,
 * что кнопка «Суммарный профиль по договору» на «Объектах и ТУ».
 */
async function fetchContractProfile(codes, period1, period2) {
    if (!codes.length) {
        notify('По договору нет фактических кодов каналов', 'warning', 4000)
        return []
    }
    const buckets = new Map()
    for (const code of codes) {
        const res = await getEnergosferaCounter(null, period1, period2, 'hour', code)
        if (!res.isOk) {
            notify(res.message || `Нет профиля по коду ${code}`, 'warning', 4000)
            continue
        }
        for (const row of res.data?.reads || []) {
            const slot = energoSlot(row, period1, period2)
            if (!slot) continue
            const value = Number(row.valueExcell ?? row.value) || 0
            const prev = buckets.get(slot.dat_out) || {
                dat_out: slot.dat_out,
                out: 0,
                new_power: null,
                old_power: null,
            }
            prev.out += value
            buckets.set(slot.dat_out, prev)
        }
    }
    const rows = [...buckets.values()].sort((a, b) => a.dat_out.localeCompare(b.dat_out))
    if (!rows.length) notify('Энергосфера не вернула профиль', 'warning', 4000)
    return rows
}

function formatNum(v, precision = 3) {
    if (v == null || Number.isNaN(Number(v))) return '—'
    return Number(v).toLocaleString('ru-RU', {
        minimumFractionDigits: 0,
        maximumFractionDigits: precision,
    })
}

const calendarOptions = {
    applyValueMode: 'useButtons',
    showDropDownButton: true,
}

export default function ConsumptionAnalysisPage() {
    const history = useHistory()
    const {selectedDog, dogovors} = useAuth()
    const kodDog = selectedDog?.kodDog
    const ndog = selectedDog?.ndog
    const {isLarge} = useScreenSize()

    const [visiblePage, setVisiblePage] = useState(true)
    const [points, setPoints] = useState([])
    const [techPoints, setTechPoints] = useState([])
    const [selected, setSelected] = useState([])
    const [period1, setPeriod1] = useState(new Date())
    const [period2, setPeriod2] = useState(new Date())
    const [loading, setLoading] = useState(false)
    const [loadingTree, setLoadingTree] = useState(false)
    const [series, setSeries] = useState([])
    const [dogSeries, setDogSeries] = useState([])
    const [dogProfile, setDogProfile] = useState(false)
    const [factCodes, setFactCodes] = useState([])
    const [holidays, setHolidays] = useState([])

    useEffect(() => {
        if (!dogHasParameterMenu(selectedDog, '0') || !verifyAccess([2], selectedDog)) {
            setVisiblePage(false)
            history.replace(getHomeRoute(dogovors, selectedDog))
            return
        }
        setVisiblePage(true)
    }, [selectedDog, dogovors, history])

    useEffect(() => {
        if (!kodDog || !visiblePage) return
        ;(async () => {
            setLoadingTree(true)
            setSelected([])
            setSeries([])
            setDogSeries([])
            setDogProfile(false)
            setFactCodes([])
            const res = await getPointsTreePlain(kodDog, 'all')
            if (res.isOk) setPoints(res.data || [])
            else notify(res.message, 'error', 5000)

            const techRes = await getGtpPoints(kodDog)
            if (techRes.isOk) setTechPoints(Array.isArray(techRes.data) ? techRes.data : [])
            else setTechPoints([])

            const factRes = await getGtpFactCodes(kodDog)
            if (factRes.isOk) {
                setFactCodes((factRes.data || []).map((r) => r.code_fact).filter(Boolean))
            } else {
                setFactCodes([])
            }

            const periodRes = await getCurrentPeriod(kodDog)
            if (periodRes.isOk && periodRes.data?.period) {
                setPeriod1(getFirstDayOfMounth(new Date(periodRes.data.period)))
                setPeriod2(getLastDayOfMounth(new Date(periodRes.data.period), false))
            }
            setLoadingTree(false)
        })()
    }, [kodDog, visiblePage])

    const chartData = useMemo(() => {
        if (!series.length && !dogSeries.length) return []
        const map = new Map()
        const ensure = (datOut) => {
            if (!map.has(datOut)) map.set(datOut, {dat_out: datOut})
            return map.get(datOut)
        }
        const maxOut = Math.max(
            0,
            ...series.map((r) => Number(r.out) || 0),
            ...dogSeries.map((r) => Number(r.out) || 0),
        )
        const markPeaks = (r, row) => {
            if (isFactPeak(r)) row[FACT_NAME] = maxOut
            if (isPlanPeak(r)) row[PLAN_NAME] = maxOut
        }
        for (const r of series) {
            const row = ensure(r.dat_out)
            row.out = Number(r.out) || 0
            row.new_power = r.new_power
            row.old_power = r.old_power
            markPeaks(r, row)
        }
        for (const r of dogSeries) {
            const row = ensure(r.dat_out)
            row.dog = Number(r.out) || 0
            markPeaks(r, row)
        }
        return [...map.values()].sort((a, b) => String(a.dat_out).localeCompare(String(b.dat_out)))
    }, [series, dogSeries])

    const holidayStripsData = useMemo(() => holidayDateStrips(holidays), [holidays])

    const kpis = useMemo(() => calcPowerKpis(series.length ? series : dogSeries), [series, dogSeries])
    const kpiHasPeaks = useMemo(() => {
        const src = series.length ? series : dogSeries
        return src.some((r) => r.new_power != null || r.old_power != null)
    }, [series, dogSeries])
    const canShow = selected.length > 0 || dogProfile

    const loadProfile = useCallback(async () => {
        if (!selected.length && !dogProfile) {
            notify('Отметьте точки учета (+/−) или суммарный профиль по договору', 'warning', 4000)
            return
        }
        setLoading(true)
        try {
            const buckets = new Map()
            for (const s of selected) {
                if (s.isTech && !s.pointCode) {
                    notify('У технической точки нет кода канала', 'warning', 4000)
                    continue
                }
                const res = s.isTech
                    ? await getEnergosferaCounter(null, period1, period2, 'hour', s.pointCode)
                    : await getEnergosferaCounter(s.kodPoint, period1, period2, 'hour')
                const reads = res.isOk ? (res.data?.reads || []) : []
                if (!res.isOk) notify(res.message || 'Нет профиля Энергосферы', 'warning', 4000)
                for (const row of reads) {
                    const slot = energoSlot(row, period1, period2)
                    if (!slot) continue
                    const value = (Number(row.valueExcell ?? row.value) || 0) * (s.sign === -1 ? -1 : 1)
                    const prev = buckets.get(slot.dat_out) || {
                        dat_out: slot.dat_out,
                        out: 0,
                        new_power: null,
                        old_power: null,
                    }
                    prev.out += value
                    buckets.set(slot.dat_out, prev)
                }
            }

            const commercial = selected
                .filter((s) => !s.isTech && Number.isFinite(Number(s.kodPoint)))
                .map((s) => ({kod_point: Number(s.kodPoint), sign: s.sign}))
            let holidayList = []
            let holidaysLoaded = false
            if (commercial.length) {
                const peakRes = await getOutValsSigned(commercial, period1, period2, true)
                if (peakRes.isOk) {
                    stampPeakFlags(buckets, peakRes.data?.series)
                    holidayList = peakRes.data?.holidays || []
                    holidaysLoaded = true
                }
            }

            const seriesRows = [...buckets.values()].sort((a, b) => a.dat_out.localeCompare(b.dat_out))
            const dogRows = dogProfile ? await fetchContractProfile(factCodes, period1, period2) : []
            if (dogRows.length) {
                const peakRes = await getOutValsSigned([], period1, period2, true, {flagsOnly: true, kodDog})
                if (peakRes.isOk) {
                    stampPeakFlags(dogRows, peakRes.data?.series)
                    if (!holidaysLoaded) {
                        holidayList = peakRes.data?.holidays || []
                        holidaysLoaded = true
                    }
                }
            }
            if (!holidaysLoaded) {
                const holidayRes = await getOutValsSigned([], period1, period2, true)
                if (holidayRes.isOk) holidayList = holidayRes.data?.holidays || []
            }
            setHolidays(holidayList)

            if (selected.length && !seriesRows.length) notify('Энергосфера не вернула почасовой профиль', 'warning', 4000)
            setSeries(selected.length ? seriesRows : [])
            setDogSeries(dogRows)
        } finally {
            setLoading(false)
        }
    }, [selected, period1, period2, dogProfile, factCodes, kodDog])

    const exportExcel = useCallback(async () => {
        if (!series.length && !dogSeries.length) {
            notify('Нет данных для выгрузки', 'warning', 4000)
            return
        }
        await exportConsumptionExcel({
            series,
            dogSeries,
            holidays,
            period1,
            period2,
            ndog,
            subtitle: selectedDog?.name || '',
        })
    }, [series, dogSeries, holidays, period1, period2, ndog, selectedDog?.name])

    if (!visiblePage) return <AccessDenied/>

    return (
        <div className="content-block dx-card responsive-paddings big-input consumption-analysis">
            <FontAwesomeIcon className="icon-header-h3 main-color main-color-light" icon={faChartLine}/>
            <h3><strong><ZagolovokFromMenu/></strong></h3>

            <div className="row mt20">
                {!isLarge ? (
                    <div className="col-md-12">
                        <LoadPanel visible={loadingTree} position={{of: '#ddb-sign-points'}}/>
                        <PopupSignPointsTree
                            points={points}
                            technicalPoints={techPoints}
                            selected={selected}
                            onChange={setSelected}
                            onShow={loadProfile}
                            showDisabled={!canShow || loading}
                            dogProfile={dogProfile}
                            onDogProfileChange={setDogProfile}
                        />
                    </div>
                ) : (
                    <div className="col-lg-3" id="sign-points-tree-wrap">
                        <LoadPanel visible={loadingTree} position={{of: '#sign-points-tree-wrap'}}/>
                        <div className="consumption-analysis-tree">
                            <SignPointsTree
                                points={points}
                                technicalPoints={techPoints}
                                value={selected}
                                onChange={setSelected}
                                dogProfile={dogProfile}
                                onDogProfileChange={setDogProfile}
                            />
                        </div>
                    </div>
                )}

                <div className={(isLarge ? 'col-9' : 'col-12') + ' mt20lgx tree-hook-left'}>
                    <div className="row" style={{alignItems: 'baseline'}}>
                        <div className="col-xl-2 col-lg-2 col-md-3 col-sm-6 col-xs-12" style={{minWidth: 200}}>
                            <DateBox
                                calendarOptions={calendarOptions}
                                openOnFieldClick={true}
                                label="Период с"
                                hint="Период с"
                                labelMode="floating"
                                displayFormat="dd.MM.yyyy"
                                useMaskBehavior={true}
                                value={period1}
                                max={period2}
                                onValueChanged={(e) => {
                                    if (e.value) setPeriod1(new Date(new Date(e.value).setHours(3, 0, 0, 0)))
                                }}
                            />
                        </div>
                        <div className="col-xl-2 col-lg-2 col-md-3 col-sm-6 col-xs-12 mt20xs" style={{minWidth: 200}}>
                            <DateBox
                                calendarOptions={calendarOptions}
                                openOnFieldClick={true}
                                label="Период по"
                                hint="Период по"
                                labelMode="floating"
                                displayFormat="dd.MM.yyyy"
                                useMaskBehavior={true}
                                value={period2}
                                min={period1}
                                onValueChanged={(e) => {
                                    if (e.value) setPeriod2(new Date(new Date(e.value).setHours(3, 0, 0, 0)))
                                }}
                            />
                        </div>
                        {isLarge && (
                            <div className="col-lg-3 col-md-3 col-sm-5 col-xs-12 mt20md mt20xs" style={{minWidth: 200}}>
                                <Button
                                    className="w-100"
                                    text="Показать"
                                    type="default"
                                    hint="Показать"
                                    icon="refresh"
                                    disabled={!canShow}
                                    onClick={loadProfile}
                                />
                            </div>
                        )}
                        <div className="col-lg-3 col-md-3 col-sm-5 col-xs-12 mt20md mt20xs" style={{minWidth: 200}}>
                            <Button
                                className="w-100"
                                text="Выгрузить в Excel"
                                hint="Выгрузить в Excel"
                                icon="xlsfile"
                                disabled={!series.length && !dogSeries.length}
                                onClick={exportExcel}
                            />
                        </div>
                    </div>

                    <div id="consumption-chart" className="mt-3">
                        <LoadPanel visible={loading} position={{of: '#consumption-chart'}}/>
                        {chartData.length === 0 ? (
                            <div className="consumption-analysis-empty">Отметьте точки (+/−) или суммарный профиль и нажмите «Показать»</div>
                        ) : (
                            <Chart dataSource={chartData} palette="Bright">
                                <CommonSeriesSettings argumentField="dat_out" type="line">
                                    <Point visible={false}/>
                                </CommonSeriesSettings>
                                {series.length > 0 && (
                                    <Series valueField="out" name="Потребление" color="#1565c0"/>
                                )}
                                {dogSeries.length > 0 && (
                                    <Series valueField="dog" name="Суммарный профиль по договору" color="#ef6c00"/>
                                )}
                                <Series
                                    valueField={FACT_NAME}
                                    name={FACT_NAME}
                                    type="bar"
                                    color="#fbc02d"
                                    barWidth={2}
                                />
                                <Series
                                    valueField={PLAN_NAME}
                                    name={PLAN_NAME}
                                    type="bar"
                                    color="#66bb6a"
                                    barWidth={2}
                                />
                                <ArgumentAxis argumentType="datetime">
                                    {holidayStripsData.map((band) => (
                                        <Strip key={band.day} startValue={band.start} endValue={band.end} color={HOLIDAY_STRIP_COLOR}/>
                                    ))}
                                </ArgumentAxis>
                                <Legend verticalAlignment="bottom" horizontalAlignment="center"/>
                                <Tooltip enabled={true} shared={true}/>
                            </Chart>
                        )}
                    </div>

                    {(series.length > 0 || dogSeries.length > 0) && (
                        <table className="consumption-kpi-table mt-3">
                            <tbody>
                            <tr>
                                <td>Потребление электроэнергии</td>
                                <td>{formatNum(kpis.energy, 2)} кВтч</td>
                            </tr>
                            <tr>
                                <td>Мощность фактическая</td>
                                <td>{kpiHasPeaks ? `${formatNum(kpis.powerFact, 0)} кВт` : '—'}</td>
                            </tr>
                            <tr>
                                <td>Мощность сетевая</td>
                                <td>{kpiHasPeaks ? `${formatNum(kpis.powerNet, 0)} кВт` : '—'}</td>
                            </tr>
                            <tr>
                                <td>Пиковое значение мощности</td>
                                <td>{formatNum(kpis.peak, 0)} кВт</td>
                            </tr>
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    )
}
